import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { createChannelRule, updateChannelRule } from "../api/costingSettingsApi";

const DEFAULT_FORM = {
  priceLevel:              "",
  targetNetMargin:         "",
  marketingPercent:        "",
  useFreight:              "No",
  crossBorder:             "No",
  ticketingPerUnit:        "",
  assortingPerUnit:        "",
  royaltyRegion:           "Regular",
  srpRoundingRule:         "NORMAL",
  priceEnding:             "0.99",
  commissionType:          "NONE",
  commissionPercent:       "",
  freightAllowancePercent: "",
  fixedUsdFx:              "",
  usdRoundTo:              "",
};

export default function ChannelRuleModal({ editingRule, onClose, onSuccess }) {
  const [form, setForm]             = useState(DEFAULT_FORM);
  const [modalError, setModalError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Populate form when editing an existing rule
  useEffect(() => {
    if (editingRule) {
      setForm({
        priceLevel:              editingRule.priceLevel || "",
        targetNetMargin:         editingRule.targetNetMargin?.toString() || "",
        marketingPercent:        editingRule.marketingPercent?.toString() || "",
        useFreight:              editingRule.useFreight ? "Yes" : "No",
        crossBorder:             editingRule.crossBorder ? "Yes" : "No",
        ticketingPerUnit:        editingRule.ticketingPerUnit?.toString() || "",
        assortingPerUnit:        editingRule.assortingPerUnit?.toString() || "",
        royaltyRegion:           editingRule.royaltyRegion || "Regular",
        srpRoundingRule:         editingRule.srpRoundingRule || "NORMAL",
        priceEnding:             editingRule.priceEnding?.toString() || "0.99",
        commissionType:          editingRule.commissionType || "NONE",
        commissionPercent:       editingRule.commissionPercent?.toString() || "",
        freightAllowancePercent: editingRule.freightAllowancePercent?.toString() || "",
        fixedUsdFx:              editingRule.fixedUsdFx?.toString() || "",
        usdRoundTo:              editingRule.usdRoundTo?.toString() || "",
      });
    } else {
      setForm(DEFAULT_FORM);
    }
    setModalError(null);
  }, [editingRule]);

  function handleChange(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setModalError(null);

    try {
      const payload = {
        ...form,
        targetNetMargin:         parseFloat(form.targetNetMargin) || 0,
        marketingPercent:        parseFloat(form.marketingPercent) || 0,
        ticketingPerUnit:        parseFloat(form.ticketingPerUnit) || 0,
        assortingPerUnit:        parseFloat(form.assortingPerUnit) || 0,
        commissionPercent:       parseFloat(form.commissionPercent) || 0,
        freightAllowancePercent: parseFloat(form.freightAllowancePercent) || 0,
        fixedUsdFx:              parseFloat(form.fixedUsdFx) || undefined,
        usdRoundTo:              parseFloat(form.usdRoundTo) || undefined,
        priceEnding:             parseFloat(form.priceEnding) || 0.99,
      };

      const json = editingRule
        ? await updateChannelRule(editingRule._id, payload)
        : await createChannelRule(payload);

      if (!json.success) throw new Error(json.message || "Unknown server error");

      toast.success(editingRule ? "Channel rule updated successfully!" : "Channel rule created successfully!");
      onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        (Array.isArray(err.response?.data?.errors)
          ? err.response.data.errors.map((e) => e.msg || e.message || e).join(", ")
          : null) ||
        err.message ||
        "Something went wrong";
      setModalError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl w-[700px] max-w-5xl max-h-[92vh] overflow-hidden">

        {/* Header */}
        <div className="px-8 py-5 border-b flex items-center justify-between bg-gray-50">
          <h3 className="text-2xl font-bold text-gray-900">
            {editingRule ? "Edit Channel Rule" : "Add New Channel Rule"}
          </h3>
          <button onClick={onClose} className="text-3xl text-gray-500 hover:text-gray-700">
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8 overflow-y-auto max-h-[calc(92vh-130px)]">

          {/* Inline backend error banner */}
          {modalError && (
            <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-2xl px-5 py-4">
              <span className="text-lg leading-none mt-0.5">⚠</span>
              <div className="flex-1 text-sm font-medium">{modalError}</div>
              <button
                type="button"
                onClick={() => setModalError(null)}
                className="text-red-400 hover:text-red-600 text-lg leading-none"
              >
                ×
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Price Level</label>
              <input
                value={form.priceLevel}
                onChange={(e) => handleChange("priceLevel", e.target.value)}
                placeholder="e.g. Wholesale Tier 2"
                className="w-full rounded-2xl px-4 py-3 border border-gray-300 focus:border-black"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Target Net Margin %</label>
              <input
                type="number" min="0" step="0.01"
                value={form.targetNetMargin}
                onChange={(e) => handleChange("targetNetMargin", e.target.value)}
                placeholder="e.g. 25"
                className="w-full rounded-2xl px-4 py-3 border border-gray-300 focus:border-black"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Marketing %</label>
              <input
                type="number" min="0" step="0.01"
                value={form.marketingPercent}
                onChange={(e) => handleChange("marketingPercent", e.target.value)}
                placeholder="e.g. 10"
                className="w-full rounded-2xl px-4 py-3 border border-gray-300 focus:border-black"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Use Freight?</label>
              <select
                value={form.useFreight}
                onChange={(e) => handleChange("useFreight", e.target.value)}
                className="w-full rounded-2xl px-4 py-3 border border-gray-300 focus:border-black"
              >
                <option>Yes</option>
                <option>No</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Use Cross-Border?</label>
              <select
                value={form.crossBorder}
                onChange={(e) => handleChange("crossBorder", e.target.value)}
                className="w-full rounded-2xl px-4 py-3 border border-gray-300 focus:border-black"
              >
                <option>Yes</option>
                <option>No</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ticketing $/Unit</label>
              <input
                type="number" min="0" step="0.01"
                value={form.ticketingPerUnit}
                onChange={(e) => handleChange("ticketingPerUnit", e.target.value)}
                placeholder="e.g. 0.75"
                className="w-full rounded-2xl px-4 py-3 border border-gray-300 focus:border-black"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Assorting $/Unit</label>
              <input
                type="number" min="0" step="0.01"
                value={form.assortingPerUnit}
                onChange={(e) => handleChange("assortingPerUnit", e.target.value)}
                placeholder="e.g. 0.35"
                className="w-full rounded-2xl px-4 py-3 border border-gray-300 focus:border-black"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Royalty Region</label>
              <select
                value={form.royaltyRegion}
                onChange={(e) => handleChange("royaltyRegion", e.target.value)}
                className="w-full rounded-2xl px-4 py-3 border border-gray-300 focus:border-black"
              >
                <option>Regular</option>
                <option>MENA</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">SRP Rounding Rule</label>
              <select
                value={form.srpRoundingRule}
                onChange={(e) => handleChange("srpRoundingRule", e.target.value)}
                className="w-full rounded-2xl px-4 py-3 border border-gray-300 focus:border-black"
              >
                <option value="NORMAL">NORMAL</option>
                <option value="END_099">END in 0.99</option>
                <option value="END_095">END in 0.95</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Price Ending Rule</label>
              <select
                value={form.priceEnding}
                onChange={(e) => handleChange("priceEnding", e.target.value)}
                className="w-full rounded-2xl px-4 py-3 border border-gray-300 focus:border-black"
              >
                <option value="0.99">End in 0.99</option>
                <option value="0">End in 0.00</option>
                <option value="0.95">End in 0.95</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Commission Type</label>
              <select
                value={form.commissionType}
                onChange={(e) => handleChange("commissionType", e.target.value)}
                className="w-full rounded-2xl px-4 py-3 border border-gray-300 focus:border-black"
              >
                <option value="NONE">NONE</option>
                <option value="FIXED">FIXED</option>
                <option value="TIERED">TIERED</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Commission %</label>
              <input
                type="number" min="0" step="0.01"
                value={form.commissionPercent}
                onChange={(e) => handleChange("commissionPercent", e.target.value)}
                placeholder="e.g. 5"
                className="w-full rounded-2xl px-4 py-3 border border-gray-300 focus:border-black"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Freight Allowance %</label>
              <input
                type="number" min="0" step="0.01"
                value={form.freightAllowancePercent}
                onChange={(e) => handleChange("freightAllowancePercent", e.target.value)}
                placeholder="e.g. 3"
                className="w-full rounded-2xl px-4 py-3 border border-gray-300 focus:border-black"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fixed USD FX vs CAD</label>
              <input
                type="number" min="0" step="0.01"
                value={form.fixedUsdFx}
                onChange={(e) => handleChange("fixedUsdFx", e.target.value)}
                placeholder="e.g. 1.38"
                className="w-full rounded-2xl px-4 py-3 border border-gray-300 focus:border-black"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">USD Round To</label>
              <input
                value={form.usdRoundTo}
                onChange={(e) => handleChange("usdRoundTo", e.target.value)}
                placeholder="e.g. 0.05"
                min="0" step="0.01"
                className="w-full rounded-2xl px-4 py-3 border border-gray-300 focus:border-black"
              />
            </div>

          </div>

          {/* Footer Buttons */}
          <div className="flex justify-end gap-4 mt-10 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-8 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-2xl"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`px-8 py-3 font-semibold rounded-2xl ${
                submitting ? "bg-gray-400 cursor-not-allowed" : "bg-black hover:bg-gray-800"
              } text-white`}
            >
              {submitting ? "Saving..." : editingRule ? "Update Rule" : "Save Rule"}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}